import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from './ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES } from '../constants';
import { fetchWithRetry } from '../utils/tourUtils';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle, 
  Camera, 
  Smartphone,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Eye
} from 'lucide-react';

const PushQueueManager = () => {
  const [pendingItems, setPendingItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const { toast } = useToast();

  useEffect(() => {
    fetchPushQueueItems();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPushQueueItems, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPushQueueItems = async () => {
    setLoading(true);
    try {
      // Fetch pending items
      const pendingResponse = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.findPendingItems',
          payload: { limit: 50 }
        },
        label: 'pending push queue items'
      });

      // Fetch all recent items (last 100)
      const allResponse = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.findMany',
          payload: { 
            orderBy: { createdAt: 'desc' },
            take: 100
          }
        },
        label: 'all push queue items'
      });

      setPendingItems(pendingResponse || []);
      setAllItems(allResponse || []);
    } catch (error) {
      console.error('Error fetching push queue items:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch push queue items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const approveItem = async (item) => {
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.update',
          payload: {
            id: item.id,
            data: {
              approve: true,
              reject: false,
              pushStatus: 'PUSHED'
            }
          }
        },
        label: 'approve push queue item'
      });

      toast({
        title: 'Success',
        description: 'Item approved successfully'
      });
      fetchPushQueueItems();
    } catch (error) {
      console.error('Error approving item:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve item',
        variant: 'destructive'
      });
    }
  };

  const rejectItem = async (item, reason) => {
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'pushQueue.update',
          payload: {
            id: item.id,
            data: {
              approve: false,
              reject: true,
              rejectRemark: reason,
              pushStatus: 'FAILED'
            }
          }
        },
        label: 'reject push queue item'
      });

      toast({
        title: 'Success',
        description: 'Item rejected successfully'
      });
      fetchPushQueueItems();
    } catch (error) {
      console.error('Error rejecting item:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject item',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: 'secondary', icon: Clock, text: 'Pending' },
      PUSHING: { variant: 'default', icon: Clock, text: 'Syncing' },
      PUSHED: { variant: 'default', icon: CheckCircle, text: 'Success', className: 'bg-green-100 text-green-800' },
      FAILED: { variant: 'destructive', icon: XCircle, text: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getChangeTypeIcon = (changeType) => {
    const icons = {
      ITEM_UPDATE: MessageSquare,
      ATTACHMENT_ADDED: Camera,
      APPROVAL_CHANGED: ThumbsUp,
      REMARK_ADDED: MessageSquare
    };
    return icons[changeType] || MessageSquare;
  };

  const getAttachmentTypeIcon = (attachmentType) => {
    if (attachmentType === 'IMAGE360') return Camera;
    if (attachmentType === 'TABLETIMAGE') return Smartphone;
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const PushQueueItemCard = ({ item, showActions = false }) => {
    const ChangeIcon = getChangeTypeIcon(item.changeType);
    const AttachmentIcon = getAttachmentTypeIcon(item.attachmentType);

    return (
      <Card key={item.id} className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChangeIcon className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-sm">
                {item.changeType.replace('_', ' ')}
              </span>
              {AttachmentIcon && (
                <AttachmentIcon className="w-4 h-4 text-gray-500" />
              )}
            </div>
            {getStatusBadge(item.pushStatus)}
          </div>

          <div className="space-y-2 text-sm text-gray-600">
            <div>
              <strong>User:</strong> {item.userId}
            </div>
            <div>
              <strong>Space ID:</strong> {item.spaceId}
            </div>
            {item.changeData && (
              <div>
                <strong>Changes:</strong>
                <pre className="bg-gray-50 p-2 rounded text-xs mt-1 overflow-x-auto">
                  {typeof item.changeData === 'string' 
                    ? item.changeData 
                    : JSON.stringify(item.changeData, null, 2)
                  }
                </pre>
              </div>
            )}
            {item.remark && (
              <div>
                <strong>Remark:</strong> {item.remark}
              </div>
            )}
            {item.rejectRemark && (
              <div className="text-red-600">
                <strong>Reject Reason:</strong> {item.rejectRemark}
              </div>
            )}
            <div className="text-xs text-gray-500">
              Created: {formatDate(item.createdAt)}
            </div>
          </div>

          {showActions && item.pushStatus === 'PENDING' && (
            <div className="flex gap-2 mt-4">
              <Button
                size="sm"
                onClick={() => approveItem(item)}
                className="bg-green-600 hover:bg-green-700"
              >
                <ThumbsUp className="w-3 h-3 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  const reason = prompt('Reject reason:');
                  if (reason) rejectItem(item, reason);
                }}
              >
                <ThumbsDown className="w-3 h-3 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedItem(item)}
              >
                <Eye className="w-3 h-3 mr-1" />
                Details
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Push Queue Management</h1>
        <Button onClick={fetchPushQueueItems} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold">
              {pendingItems.length}
            </div>
            <div className="text-sm text-gray-600">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold">
              {allItems.filter(item => item.pushStatus === 'PUSHED').length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <div className="text-2xl font-bold">
              {allItems.filter(item => item.pushStatus === 'FAILED').length}
            </div>
            <div className="text-sm text-gray-600">Failed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">
              {allItems.filter(item => item.changeType === 'ATTACHMENT_ADDED').length}
            </div>
            <div className="text-sm text-gray-600">Attachments</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pending">
            Pending Items ({pendingItems.length})
          </TabsTrigger>
          <TabsTrigger value="all">
            All Items ({allItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="space-y-4">
            {pendingItems.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No pending items</p>
                </CardContent>
              </Card>
            ) : (
              pendingItems.map(item => (
                <PushQueueItemCard key={item.id} item={item} showActions={true} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="space-y-4">
            {allItems.map(item => (
              <PushQueueItemCard key={item.id} item={item} showActions={false} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Item Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">Item Details</h2>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedItem(null)}
                >
                  Close
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="font-medium">ID:</label>
                  <p className="text-sm font-mono bg-gray-100 p-2 rounded">{selectedItem.id}</p>
                </div>
                
                <div>
                  <label className="font-medium">Full Data:</label>
                  <pre className="text-xs bg-gray-100 p-4 rounded overflow-x-auto">
                    {JSON.stringify(selectedItem, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PushQueueManager;

